import { forwardRef } from "react"
import {
  Button as HeadlessButton,
  ButtonProps as HeadlessButtonProps,
} from "@headlessui/react"
import { clsx } from "clsx"

import { Icon } from "@src/components/atoms/Icon"
import { sizeClasses } from "@src/components/atoms/Icon/utils"
import { IconType } from "@src/config/icons"

const styles = {
  base: [
    // base
    "inline-flex items-center justify-center border cursor-pointer disabled:pointer-events-none rounded-lg",

    // sizing
    "p-0",

    // focus
    "focus:outline-none data-[focus]:outline data-[focus]:outline-2 data-[focus]:outline-offset-2 data-[focus]:outline-blue",

    // disabled
    "data-[disabled]:opacity-25",
  ],

  primary: [
    // base
    "bg-blue text-white border-blue font-semibold",

    // actions
    "hover:bg-blue-dark hover:border-blue-dark active:border-blue-lighter active:bg-blue-lighter",
  ],

  secondary: [
    // base
    "bg-transparent text-pearl-dark border-pearl-light font-semibold",

    // actions
    "hover:bg-pearl hover:text-pearl-darker",
  ],

  ternary: [
    // base
    "bg-pearl/20 border-transparent text-pearl-light font-semibold",

    // actions
    "hover:bg-pearl/10 active:bg-transparent active:text-blue-dark",
  ],

  translucent: [
    // base
    "bg-black/50 text-white border-transparent font-semibold",

    // actions
    "hover:bg-black/60 active:bg-black/80",
  ],

  text: [
    // base
    "bg-transparent border-transparent",

    // actions
    "hover:text-blue-dark active:text-blue-dark",
  ],

  textUnstyled: [
    // base
    "bg-transparent border-transparent",
  ],

  dashed: [
    // base
    "bg-transparent border-dashed border-blue",

    // actions
    "hover:text-blue-dark active:text-blue-dark hover:bg-blue-light/10",
  ],
}

export type IconButtonProps = {
  size?: keyof typeof sizeClasses
  variant?:
    | "primary"
    | "secondary"
    | "ternary"
    | "text"
    | "dashed"
    | "translucent"
    | "textUnstyled"
  className?: string
  icon: IconType
  iconClasses?: string
  "aria-label": string
} & HeadlessButtonProps

export const IconButton = forwardRef<HTMLElement, IconButtonProps>(
  (
    {
      variant = "primary",
      className,
      icon,
      size = "default",
      iconClasses,
      ...props
    },
    ref,
  ) => {
    const classes = clsx(styles.base, styles[variant], className)

    return (
      <HeadlessButton ref={ref} className={classes} {...props}>
        {icon && (
          <Icon
            className={clsx(iconClasses, "leading-none")}
            size={size}
            icon={icon}
          />
        )}
      </HeadlessButton>
    )
  },
)

IconButton.displayName = "IconButton"
